/**
 * Control de acceso por rol (RBAC).
 *
 * Fuente de verdad: **"Matriz_Roles-Permisos_Ares v1.0-2.xlsx"**, hoja `Matriz`.
 * Los roles se identifican con el **nombre del grupo de Cognito en CamelCase**
 * (`CustomerServiceAnalyst`, `ControlDeskCoordinator`, …). El lookup normaliza a minúsculas, así
 * que da igual la capitalización con que Cognito los mande en el token.
 *
 * OJO — corrección respecto a la versión anterior de este archivo: las hojas `Matriz` y
 * `Endpoints` **no coinciden al 100%**. Verificado celda por celda contra v1.0-2:
 *
 *  - 10 de las 11 filas de roles coinciden exacto. `Endpoints` solo tiene 3 columnas insertadas
 *    ("Manuel/Alex" en C, y "Finanzas - Reportos" / "Finanzas - Facturación" antes de Seguridad),
 *    por eso todo se corre +1 y luego +3.
 *  - La fila **ATC-ANL diverge**: `Endpoints` le da 6 permisos que `Matriz` no le da
 *    (`organizations.changeFee`, `organizations.create`, `transactions.createCounterparty`,
 *    `finance.returns`, `operations.unitMovement`, `security.blockUser`) y le quita 3 que
 *    `Matriz` sí le da (`prospects.view`, `accounts.viewClabe`, `organizations.viewCustomerOrg`).
 *  - Se toma `Matriz` como autoridad: es la hoja que lleva los comentarios de negocio
 *    (Paola Portilla, 2026-08-18) y la que da el criterio de menor privilegio. Ver
 *    `docs/roles-permisos-ares.md` §5 — pendiente de firma de negocio.
 *
 * Hay DOS capas y conviene no confundirlas:
 *
 *  1. `APP_PERMISSIONS` + `ROLE_PERMISSIONS` — los 33 permisos granulares del Excel. Es la capa
 *     nueva y la que debe crecer. Se consulta con `RoleManager.can(roles, 'clients.create')`, o
 *     desde plantillas con `*appHasPermission` (ver `shared/directives/has-permission.directive.ts`).
 *  2. `ROLE_CONFIGS` / `RoleConfig` — la capa vieja de 9 booleanos gruesos. **Ya se eliminó**
 *     (ver la nota al final del archivo); no queda nada que la consuma.
 *
 * `SECTION_ROLES` (lo que leen las rutas vía `data.roles`) ya NO se escribe a mano: se **deriva**
 * de `ROLE_PERMISSIONS`, así que agregar un rol a la matriz actualiza rutas y menú de una vez.
 */

// ─────────────────────────────────────────────────────────────────────────────
// 1. Catálogo de permisos (las 33 columnas del Excel, en orden)
// ─────────────────────────────────────────────────────────────────────────────

export const APP_PERMISSIONS = [
    // Clientes
    'clients.view', // Clientes - Ver información cliente
    'clients.create', // Clientes - Crear cliente
    'clients.update', // Clientes - Actualizar cliente
    'prospects.view', // Clientes - Ver prospectos/documentos
    'prospects.create', // Clientes - Crear prospectos/documentos
    /**
     * Aprobar o rechazar un PROSPECTO (en ARES, convertirlo en cliente; en aura, el par
     * APROBAR/RECHAZAR del detalle). No sale del Excel: lo acotó negocio a Mesa de Control e IT.
     * Antes no estaba gateado y cualquier rol que llegara al detalle veía los botones — ATC entre
     * ellos, que solo debe consultar.
     */
    'prospects.approve', // Clientes - Aprobar/rechazar prospecto
    // Documentos
    'documents.view', // Documentos - Ver documentos
    'documents.upload', // Documentos - Subir documentación
    'documents.download', // Documentos - Descargar documentación
    /**
     * Aprobar o rechazar un DOCUMENTO. Ojo: no alcanza con `documents.upload` — ATC puede subir
     * documentación de un prospecto pero no es quien la dictamina.
     */
    'documents.review', // Documentos - Aprobar/rechazar documentos
    /**
     * Ver el histórico de versiones de un documento (el panel desplegable del expediente en el
     * detalle de cliente). No sale del Excel: lo pidió negocio aparte, restringido a IT.
     * Sin él, la pantalla muestra únicamente la versión vigente y ni siquiera insinúa que existan
     * anteriores — nada de contador de versiones ni de botón para desplegar.
     */
    'documents.viewHistory', // Documentos - Ver histórico de versiones
    // Cuentas / KYC
    'accounts.viewClabe', // Cuentas/KYC - Ver cuenta CLABE
    'accounts.viewBalance', // Cuentas/KYC - Ver saldo disponible
    'accounts.editCover', // Cuentas/KYC - Editar carátula
    'kyc.view', // Cuentas/KYC - Ver KYC / perfil de riesgo
    'kyc.update', // Cuentas/KYC - Actualizar KYC / perfil de riesgo
    // Organizaciones
    'organizations.viewCustomerOrg', // Organizaciones - Ver organización del cliente
    'organizations.viewFee', // Organizaciones - Ver Fee apertura/cliente
    'organizations.changeFee', // Organizaciones - Cambiar Fee cliente
    'organizations.view', // Organizaciones - Ver organizaciones
    'organizations.create', // Organizaciones - Crear organizaciones
    // Transacciones
    'transactions.view', // Transacciones - Ver transacciones del cliente
    'transactions.viewLogs', // Transacciones - Logs de transacción
    'transactions.downloadReport', // Transacciones - Descargar reporte de transacciones
    'transactions.downloadReportPerCustomer', // Transacciones - Descargar reporte de transacciones
    'transactions.downloadStatements', // Transacciones - Descargar estados de cuenta
    'transactions.createCounterparty', // Transacciones - Crear contraparte
    'operations.unitMovement', // Transacciones - Cargos y abonos unitarios
    'transactions.viewAlerts', // Transacciones - Ver alertas (Logs de TX)
    // Finanzas — columnas que SOLO existen en la hoja `Endpoints` (AC y AD), y que ahí están
    // vacías para los 11 roles. O sea: nadie las tiene por matriz; solo las alcanzan los roles
    // de acceso total (god / superadmin / TechnologyDirector / admin) más `reporto`, que es un grupo de
    // Cognito preexistente creado exactamente para la pantalla de Reportos.
    'finance.reportos', // Finanzas - Reportos
    'finance.invoicing', // Finanzas - Facturación
    'finance.returns', // Finanzas - Retornos
    'finance.invoicing', // Finanzas - Facturación
    'finance.stp', // Finanzas - STP
    'finance.accounts', // Finanzas - Cuentas de ingresos
    'finance.transactions', // Finanzas - Reporte de transacciones
    // Seguridad
    'security.viewLocationLogs', // Seguridad - Ver log ubicación/login
    'security.resetMfa', // Seguridad - Reset MFA/TOTP (Opciones)
    'security.blockUser', // Seguridad - Bloquear usuario (Opciones)
    'security.unlogUser', // Seguridad - Desloguear usuario (Opciones)
    // Administración de la propia matriz — columna AK de `Endpoints`, solo IT-SOP (`TechnologySupport`).
    // Todavía no hay pantalla que la consuma; queda declarada para no perderla.
    'roles.crud', // CRUD ROLES

    'users.view',
    'users.create',
    'users.update'
] as const;

export type AppPermission = (typeof APP_PERMISSIONS)[number];

/** Acceso total. `god` y `admin` por indicación del usuario; `TechnologyDirector`/`superadmin` porque el Excel les marca todas. */
const ALL: readonly AppPermission[] = APP_PERMISSIONS;

/**
 * Acceso total MENOS los permisos indicados.
 *
 * Existe para los permisos que negocio acotó a un grupo concreto y que, por tanto, no deben
 * heredarse por ser "acceso total": hasta que los analistas de ATC/Operaciones/Cumplimiento se
 * muevan a su grupo de la matriz siguen entrando como `admin` (ver el RIESGO ABIERTO más abajo),
 * así que dejarlos dentro de `ALL` se los daría justo a quienes se les quiere negar.
 *
 * Hoy: `documents.viewHistory`, `documents.review` y `prospects.approve` — los tres acotados a
 * Mesa de Control (`ControlDeskCoordinator`) e IT (`god`, `TechnologyDirector`, y el histórico
 * también a `TechnologySupport`).
 */
const allExcept = (...excluded: readonly AppPermission[]): readonly AppPermission[] => APP_PERMISSIONS.filter((permission) => !excluded.includes(permission));

// ─────────────────────────────────────────────────────────────────────────────
// 2. Matriz rol → permisos
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Permisos por rol, tal cual el Excel.
 *
 * Las claves están escritas **igual que el grupo de Cognito**, en CamelCase
 * (`CustomerServiceAnalyst`, `FinanceDirector`, …). Antes eran los códigos cortos del Excel
 * (`atc-anl`, `fin-dir`), pero tener la clave distinta del grupo real es justo lo que provocó
 * el bug de `ACT-ANL`: el rol llegaba del token, no calzaba con ninguna clave y `RoleGuard`
 * mandaba al usuario a `/landing/access`. El lookup normaliza a minúsculas (ver
 * `RoleManager.index()`), así que da igual cómo venga el grupo en el token.
 *
 * Los códigos cortos del Excel siguen sirviendo como alias — ver `LEGACY_ROLE_ALIASES`, que
 * también documenta los roles que Cognito manda **hoy** (columna "Perfil de Usuario" de `Hoja1`).
 */
export const ROLE_PERMISSIONS: Record<string, readonly AppPermission[]> = {
    // ── Roles de la matriz ────────────────────────────────────────────────────
    /**
     * ATC — Atención a Clientes (hoja `Matriz`, fila 2).
     *
     * Es la ÚNICA fila donde `Matriz` y `Endpoints` no coinciden, y se resolvió a favor de
     * `Matriz` (menor privilegio + es la hoja comentada por negocio). Respecto a lo que este
     * archivo tenía antes (que venía de `Endpoints`):
     *   - QUITADOS: organizations.changeFee, organizations.create, transactions.createCounterparty,
     *     finance.returns, operations.unitMovement, security.blockUser.
     *   - AGREGADOS: prospects.view, accounts.viewClabe, organizations.viewCustomerOrg.
     * Si negocio confirma que manda `Endpoints`, revertir es solo mover esas 9 líneas.
     *
     * Comentarios de negocio sobre esta fila (Paola Portilla, 2026-08-18):
     *   - "Logren ver ORG y FEE"  → por eso viewCustomerOrg + viewFee, y NO changeFee.
     *   - "Solo para prospecto" / "Pueden ver documentos" → sobre `prospects.view`.
     *   - "Reset solo para prospecto no para cliente" + "Agregar responsiva" → sobre
     *     `security.resetMfa`. La distinción prospecto/cliente NO se puede expresar con un
     *     permiso plano; hoy `security.resetMfa` habilita ambos. Ver docs §6, pendiente.
     * Observación del Excel: "Reset MFA y bloqueo y deslogueo de usuarios los solicita Gerente de ATC".
     */
    CustomerServiceAnalyst: [
        'accounts.viewBalance',
        'clients.view',
        'prospects.view',
        'documents.view',
        'documents.upload',
        'accounts.viewClabe',
        'organizations.viewCustomerOrg',
        'organizations.viewFee',
        'organizations.view',
        'transactions.view',
        'transactions.viewLogs',
        'transactions.downloadReport',
        'transactions.downloadStatements',
        'transactions.downloadReportPerCustomer',
        'security.viewLocationLogs',
        'security.resetMfa',
        'security.unlogUser',
        'prospects.create'
    ],

    /**
     * MC/AR — Mesa de Control / Asuntos Regulatorios.
     *
     * OJO: la fila del Excel se corta en "Ver Fee apertura/cliente", así que este rol queda SIN
     * ningún permiso de Transacciones ni de Seguridad. Puede ser intencional o una fila a medio
     * llenar — está reportado al usuario, pendiente de confirmar con negocio.
     */
    ControlDeskCoordinator: [
        'clients.view',
        'clients.create',
        'clients.update',
        'prospects.view',
        'prospects.create',
        'prospects.approve',
        'documents.view',
        'documents.upload',
        'documents.download',
        'documents.review',
        'documents.viewHistory',
        'accounts.viewClabe',
        'accounts.viewBalance',
        'accounts.editCover',
        'kyc.view',
        'kyc.update',
        'organizations.viewCustomerOrg',
        'organizations.viewFee'
    ],

    /** CUM-COR → `ComplianceCoordinator` — Cumplimiento, solo lectura. */
    ComplianceCoordinator: [
        'accounts.viewBalance',
        'accounts.viewClabe',
        'kyc.view',
        'clients.view',
        'prospects.view',
        'prospects.create',
        'documents.view',
        'organizations.viewCustomerOrg',
        'organizations.view',
        'transactions.view',
        'security.viewLocationLogs',
        'organizations.viewFee',
        'transactions.viewLogs',
        'transactions.downloadReportPerCustomer',
        'transactions.downloadStatements'
    ],

    /** IT-DIR → `TechnologyDirector` — SuperAdmin. Las 30 en el Excel. */
    TechnologyDirector: ALL,

    /** IT-SOP → `TechnologySupport` — Soporte de IT. Lectura amplia + reset de MFA, sin crear ni mover dinero. Único con CRUD de roles. */
    TechnologySupport: [
        'roles.crud',
        'clients.view',
        'prospects.view',
        'documents.view',
        'documents.download',
        'documents.viewHistory',
        'accounts.viewClabe',
        'accounts.viewBalance',
        'kyc.view',
        'organizations.viewCustomerOrg',
        'organizations.viewFee',
        'organizations.view',
        'transactions.view',
        'transactions.viewLogs',
        'transactions.downloadReport',
        'transactions.downloadReportPerCustomer',
        'transactions.downloadStatements',
        'transactions.viewAlerts',
        'security.viewLocationLogs',
        'security.resetMfa',
        'users.view',
        'users.create',
        'users.update'
    ],

    /** DG → `GeneralDirector` — Dirección General. */
    GeneralDirector: [
        'clients.view',
        'prospects.view',
        'documents.view',
        'documents.download',
        'accounts.viewClabe',
        'accounts.viewBalance',
        'kyc.view',
        'organizations.viewCustomerOrg',
        'organizations.viewFee',
        'organizations.view',
        'organizations.create',
        'transactions.view',
        'transactions.viewLogs',
        'transactions.downloadReport',
        'transactions.downloadReportPerCustomer',
        'transactions.downloadStatements',
        'transactions.createCounterparty',
        'finance.returns',
        'finance.reportos',
        'transactions.viewAlerts',
        'security.viewLocationLogs',
        'security.resetMfa',
        'security.blockUser'
    ],

    /** FIN-DIR → `FinanceDirector` — Dirección de Finanzas. Igual que DG salvo que sí mueve cargos/abonos unitarios y no bloquea usuarios. */
    FinanceDirector: [
        'clients.view',
        'prospects.view',
        'documents.view',
        'documents.download',
        'accounts.viewClabe',
        'accounts.viewBalance',
        'kyc.view',
        'organizations.viewCustomerOrg',
        'organizations.viewFee',
        'organizations.view',
        'organizations.create',
        'transactions.view',
        'transactions.viewLogs',
        'transactions.downloadReport',
        'transactions.downloadReportPerCustomer',
        'transactions.downloadStatements',
        'transactions.createCounterparty',
        'finance.returns',
        'finance.invoicing',
        'finance.reportos',
        'finance.returns',
        'operations.unitMovement',
        'transactions.viewAlerts',
        'security.viewLocationLogs',
        'security.resetMfa'
    ],

    /** FIN-SUP → `FinanceSupervisor` — Supervisión de Finanzas. Lectura acotada + descarga de estados de cuenta. */
    FinanceSupervisor: [
        'clients.view',
        'documents.view',
        'organizations.viewCustomerOrg',
        'organizations.view',
        'transactions.view',
        'transactions.downloadStatements',
        'security.viewLocationLogs',
        'finance.invoicing',
        'finance.returns',
        'transactions.viewLogs',
        'transactions.downloadReport',
        'transactions.downloadReportPerCustomer',
        'accounts.viewBalance'
    ],

    /**
     * OPE-COR → `OperationsCoordinator` — Operaciones (hoja `Matriz`, fila 11). **Faltaba por completo en este archivo**: la
     * fila existe en `Matriz` pero está vacía en `Endpoints`, y la versión anterior se apoyó en
     * `Endpoints`. Resultado: cualquier usuario del grupo `OPE-COR` quedaba sin permisos.
     *
     * Es el rol operativo más fuerte después de los de acceso total: es el único fuera de
     * god/superadmin que cambia el Fee del cliente y ejecuta cargos/abonos unitarios.
     * Comentarios de negocio: "Editar ORG y FEE" (sobre `clients.update`, o sea que su edición de
     * cliente es específicamente organización + fee) y "Solo Clientes" (sobre `documents.view`:
     * documentos de clientes, no de prospectos — de ahí que no tenga `prospects.view`).
     */
    OperationsCoordinator: [
        'clients.view',
        'clients.update',
        'documents.view',
        'accounts.viewClabe',
        'accounts.viewBalance',
        'finance.returns',
        'kyc.view',
        'kyc.update',
        'organizations.viewCustomerOrg',
        'organizations.viewFee',
        'organizations.changeFee',
        'organizations.view',
        'organizations.create',
        'transactions.view',
        'transactions.viewLogs',
        'transactions.downloadReport',
        'transactions.downloadReportPerCustomer',
        'transactions.downloadStatements',
        'transactions.createCounterparty',
        'operations.unitMovement',
        'security.blockUser',
        'security.resetMfa',
        'security.unlogUser',
        'security.viewLocationLogs'
    ],

    /**
     * COM-COR → `CommercialCoordinator` — Comercial (hoja `Matriz`, fila 12). También **faltaba**: la fila no existe en
     * `Endpoints`. Es el rol más acotado de la matriz: consulta comercial pura, sin saldo,
     * sin KYC, sin transacciones y sin ninguna acción de seguridad.
     * Comentario de negocio sobre `documents.view`: "Para clientes y prospectos".
     */
    CommercialCoordinator: [
        'clients.view',
        'prospects.view',
        'prospects.create',
        'documents.view',
        'accounts.viewClabe',
        'accounts.viewBalance',
        'transactions.viewAlerts',
        'organizations.view',
        'kyc.view',
        'transactions.viewLogs',
        'transactions.downloadReport',
        'transactions.downloadReportPerCustomer',
        'transactions.downloadStatements',
        'transactions.view',
        'organizations.viewCustomerOrg',
        'organizations.viewFee',
    ],

    // ── Roles que Cognito manda HOY ───────────────────────────────────────────
    /** Modo God. Acceso total (indicación del usuario + "Modo God" en el Excel). */
    god: ALL,
    /**
     * Acceso total por indicación del usuario, mientras se termina de aterrizar la matriz.
     *
     * RIESGO ABIERTO: en `Hoja1` del Excel, los analistas de ATC (Zindy, Soren), Operaciones (Ana)
     * y Cumplimiento (Janette, Luis Alejandro) tienen "Perfil de Usuario" = `admin`. Mientras
     * Cognito les siga mandando `admin`, tienen las 33 y la matriz no los limita en nada. La
     * matriz no empieza a aplicar hasta que esos usuarios se muevan a su grupo `*-*`
     * (ATC-ANL / OPE-COR / CUM-COR). Ver docs §7.
     */
    admin: allExcept('documents.viewHistory', 'documents.review', 'prospects.approve'),
    /** Equivalente a IT-DIR (SuperAdmin), que en el Excel tiene todas. */
    superadmin: allExcept('documents.viewHistory', 'documents.review', 'prospects.approve'),

    /**
     * `reporto` — grupo preexistente de Cognito (está en `cli-inputs.json` sobre `/reportos` y
     * `/tx`), dedicado a la pantalla de Reportos. No es un rol de la matriz: el Excel dejó la
     * columna "Finanzas - Reportos" vacía para los 11 roles.
     *
     * Estaba declarado en `finance.routes.ts` (`roles: ['reporto','god']`) pero NO en este archivo,
     * así que no entraba a `ALLOWED_ADMIN_ROLES` y `RoleGuard` lo mandaba a `/landing/access`
     * antes de llegar a la pantalla: el grupo estaba efectivamente inutilizable.
     */
    reporto: ['finance.reportos', 'transactions.view']
};

/**
 * Roles que Cognito manda hoy y que NO son códigos del Excel: se resuelven al rol de la matriz
 * con el que `Hoja1` los empareja. Son **suposiciones documentadas**, no dato duro del Excel —
 * confirmar con negocio antes de que alguien dependa de ellas.
 *
 *  - `creator`   → mc-cor : Hoja1 pone a Mesa Control (MC-SUP/MC-DIR) y Comercial (COM-COR) como `creator`.
 *  - `financial` → fin-sup: Hoja1 pone a Flor y Diego (finanzas) como `financial` / FIN-SUP.
 *  - `viewer`    → cum-cor: el perfil de la fila CUM-COR del Excel se llama literalmente "Viewer".
 *  - `ops_admin` → atc-anl: Hoja1 tiene a Leslye como `ops_admin` en el área ATC, sin código IT.
 *                  El MENOS seguro de los cuatro.
 *  - `compliance`→ cum-cor: no aparece en Hoja1; se asume cumplimiento de solo lectura.
 *
 * `ops_admin` y `opsadmin` apuntan al mismo lugar a propósito: en el código el rol se escribe
 * `opsAdmin` y en Hoja1 `ops_admin`, y al normalizar a minúsculas quedan en formas distintas.
 */
export const LEGACY_ROLE_ALIASES: Record<string, string> = {
    // ── Grupos que Cognito manda HOY ──────────────────────────────────────────
    creator: 'ControlDeskCoordinator',
    financial: 'FinanceSupervisor',
    viewer: 'ComplianceCoordinator',
    compliance: 'ComplianceCoordinator',

    // ── Códigos "ROL SISTEMA" del Excel ───────────────────────────────────────
    // Los grupos de Cognito pasaron a CamelCase, pero el Excel sigue documentando los códigos
    // cortos y hay usuarios ya asignados a ellos. Se dejan como alias para que las dos formas
    // resuelvan al mismo set de permisos y la migración de grupos se pueda hacer sin downtime.
    // Retirar cuando no quede nadie en los grupos viejos.
    'mc-cor': 'ControlDeskCoordinator',
    'cum-cor': 'ComplianceCoordinator',
    'it-dir': 'TechnologyDirector',
    'it-sop': 'TechnologySupport',
    dg: 'GeneralDirector',
    'fin-dir': 'FinanceDirector',
    'fin-sup': 'FinanceSupervisor',
    'ope-cor': 'OperationsCoordinator',
    'com-cor': 'CommercialCoordinator',

    /** Áreas de Mesa de Control que `Hoja1` lista sin fila propia en la matriz. */
    'mc-dir': 'ControlDeskCoordinator',
    'mc-sup': 'ControlDeskCoordinator'
};

// ─────────────────────────────────────────────────────────────────────────────
// 3. Secciones de la app → permisos que las habilitan
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Qué permiso hace falta para ver cada sección. Lo usan el menú (`app.menu.ts`) y, derivado,
 * `SECTION_ROLES` para las rutas. Basta **uno** de los permisos de la lista.
 *
 * `COMPLIANCE` y `FINANCE_*` son los que peor encajan con el Excel: la matriz no tiene columnas
 * para el módulo de Cumplimiento ni para la mayoría de las pantallas de Finanzas (ver la lista de
 * huecos que se le entregó al usuario). Se mapean a lo más cercano y quedan marcados como TODO.
 */
export const SECTION_PERMISSIONS = {
    CLIENTS_READ: ['clients.view'],
    /** Crear cliente nuevo. */
    CLIENTS_WRITE: ['clients.create'],
    /** Editar datos de un cliente existente (carátula, org, fee). */
    CLIENTS_EDIT: ['clients.update'],
    /** Actualización de clientes y flujo KYC. */
    CLIENTS_REVIEW: ['kyc.update'],
    /**
     * Actualización de documentos (aprobar/rechazar). Requiere los DOS permisos — kyc.update solo
     * no es suficiente (excluye OperationsCoordinator) ni documents.upload solo (excluye ATC).
     */
    CLIENTS_DOCUMENTS_REVIEW: ['documents.upload', 'kyc.update'],

    PROSPECTS_READ: ['prospects.view'],
    PROSPECTS_WRITE: ['prospects.create'],

    ORGANIZATIONS_READ: ['organizations.view'],
    ORGANIZATIONS_WRITE: ['organizations.create'],

    /** TODO: el Excel no cubre el módulo de Cumplimiento. Se aproxima con geolocalización/login. */
    COMPLIANCE: ['security.viewLocationLogs'],

    /**
     * Finanzas — lectura general (Analytics, STP, Cuentas de ingresos, Reporte de transacciones).
     * TODO: el Excel no tiene columnas para estas 4 pantallas; se aproximan con el reporte de
     * transacciones, que es lo más cercano que sí está en la matriz.
     */
    FINANCE_READ: ['finance.returns', 'transactions.downloadReport'],

    /** Retornos (SCR-028) — sí está en la matriz como "Retornos / Finanzas retornos". */
    FINANCE_RETURNS: ['finance.returns'],

    /** Reportos (SCR-024) — columna que solo existe en la hoja `Endpoints`, vacía para los 11 roles. */
    FINANCE_REPORTOS: ['finance.reportos'],

    /** Facturación (SCR-025) — misma situación que Reportos. */
    FINANCE_INVOICING: ['finance.invoicing'],

    /** Movimientos de dinero reales (SCR-029/030). */
    OPERATIONS_EXECUTE: ['operations.unitMovement'],

    /** Administración de la matriz de roles. Sin pantalla todavía; solo IT-SOP. */
    ROLES_ADMIN: ['roles.crud'],

    USERS_READ: ['users.view'],
    USERS_WRITE: ['users.create', 'users.update']
} as const satisfies Record<string, readonly AppPermission[]>;

export type SectionKey = keyof typeof SECTION_PERMISSIONS;

/**
 * Secciones en las que el usuario debe tener TODOS los permisos de la lista (no solo uno).
 * El resto usa semántica "al menos uno".
 */
const SECTION_REQUIRES_ALL = new Set<SectionKey>(['CLIENTS_DOCUMENTS_REVIEW']);

/**
 * Excepciones de acceso a secciones completas mientras la matriz no tenga permisos propios para
 * Finanzas y Cumplimiento. Un permiso puntual (por ejemplo, descargar un reporte o ver ubicación)
 * no debe abrir por accidente todo el módulo.
 */
export const SECTION_DENIED_ROLES: Partial<Record<SectionKey, readonly string[]>> = {
    FINANCE_READ: ['customerserviceanalyst'],
    COMPLIANCE: ['customerserviceanalyst', 'financesupervisor']
};

// ─────────────────────────────────────────────────────────────────────────────
// 4. Acceso a la aplicación
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Roles que pueden entrar al dashboard. Los que no estén aquí los manda `RoleGuard` a
 * `/landing/access`. Se arma con los roles de la matriz + los que Cognito manda hoy.
 *
 * Los roles con cero permisos (`card`, `cashier`, `merchant`) se conservan porque ya estaban:
 * entran a la app pero, con el menú filtrado, no verán ninguna sección.
 */
export const ALLOWED_ADMIN_ROLES = [
    ...Object.keys(ROLE_PERMISSIONS),
    ...Object.keys(LEGACY_ROLE_ALIASES),
    // Preexistentes sin equivalente en el Excel; sin permisos asignados.
    'accountadmin',
    'accountauthorizer',
    'accountoperator',
    'card',
    'cashier',
    'merchant'
];

/** Roles BLOQUEADOS explícitamente. */
export const BLOCKED_ROLES = ['customer', 'prospect', 'mfareset'];

const NORMALIZED_ALLOWED_ADMIN_ROLES = new Set(ALLOWED_ADMIN_ROLES.map((role) => role.toLowerCase()));
const NORMALIZED_BLOCKED_ROLES = new Set(BLOCKED_ROLES.map((role) => role.toLowerCase()));

// ─────────────────────────────────────────────────────────────────────────────
// 5. RoleManager
// ─────────────────────────────────────────────────────────────────────────────

export class RoleManager {
    private static normalizeRoles(roleOrRoles: string | string[]): string[] {
        const roles = Array.isArray(roleOrRoles) ? roleOrRoles : [roleOrRoles];
        return roles.filter((role) => typeof role === 'string' && !!role.trim()).map((role) => role.toLowerCase().trim());
    }

    /**
     * Índice `rol en minúsculas` → permisos, construido una vez y memorizado.
     *
     * Hace falta porque las claves de `ROLE_PERMISSIONS` y los targets de `LEGACY_ROLE_ALIASES`
     * están escritos igual que el grupo de Cognito (`CustomerServiceAnalyst`), mientras que lo que
     * llega del token ya viene normalizado a minúsculas. Así la clave del config es idéntica al
     * grupo real y no hay que mantener dos ortografías en paralelo.
     */
    private static normalizedIndex: Map<string, readonly AppPermission[]> | null = null;

    private static index(): Map<string, readonly AppPermission[]> {
        if (this.normalizedIndex) return this.normalizedIndex;

        const index = new Map<string, readonly AppPermission[]>();
        for (const [role, permissions] of Object.entries(ROLE_PERMISSIONS)) {
            index.set(role.toLowerCase(), permissions);
        }
        // Los alias se resuelven contra el índice ya poblado, así que no importa el orden en que
        // estén escritos ni si un alias apunta a un rol declarado más abajo.
        for (const [alias, target] of Object.entries(LEGACY_ROLE_ALIASES)) {
            const permissions = index.get(target.toLowerCase());
            if (permissions) index.set(alias.toLowerCase(), permissions);
        }

        this.normalizedIndex = index;
        return index;
    }

    /** Resuelve el rol al de la matriz (aplicando alias) y devuelve sus permisos. */
    private static permissionsOf(role: string): readonly AppPermission[] {
        return this.index().get(role) || [];
    }

    /** Todos los permisos efectivos del usuario (la unión, porque Cognito puede mandar varios grupos). */
    static permissionsFor(roleOrRoles: string | string[]): Set<AppPermission> {
        const result = new Set<AppPermission>();
        for (const role of this.normalizeRoles(roleOrRoles)) {
            for (const permission of this.permissionsOf(role)) result.add(permission);
        }
        return result;
    }

    /** ¿Tiene ESTE permiso? */
    static can(roleOrRoles: string | string[], permission: AppPermission): boolean {
        return this.normalizeRoles(roleOrRoles).some((role) => this.permissionsOf(role).includes(permission));
    }

    /** ¿Tiene AL MENOS UNO de estos permisos? */
    static canAny(roleOrRoles: string | string[], permissions: readonly AppPermission[]): boolean {
        return permissions.some((permission) => this.can(roleOrRoles, permission));
    }

    /** ¿Tiene TODOS estos permisos? */
    static canAll(roleOrRoles: string | string[], permissions: readonly AppPermission[]): boolean {
        return permissions.every((permission) => this.can(roleOrRoles, permission));
    }

    /** ¿Puede ver esta sección? Es lo que consultan el menú y los guards. */
    static canAccessSection(roleOrRoles: string | string[], section: SectionKey): boolean {
        const roles = this.normalizeRoles(roleOrRoles);
        const deniedRoles = SECTION_DENIED_ROLES[section] || [];
        const perms = SECTION_PERMISSIONS[section];
        const requiresAll = SECTION_REQUIRES_ALL.has(section);
        return roles.some((role) => !deniedRoles.includes(role) && (requiresAll ? this.canAll(role, perms) : this.canAny(role, perms)));
    }

    /** Roles (incluidos los alias) que tienen al menos uno de los permisos dados. */
    static rolesWithAny(permissions: readonly AppPermission[]): string[] {
        const matrixRoles = Object.keys(ROLE_PERMISSIONS).filter((role) => permissions.some((p) => ROLE_PERMISSIONS[role].includes(p)));
        const matrixLower = new Set(matrixRoles.map((role) => role.toLowerCase()));
        const aliasRoles = Object.keys(LEGACY_ROLE_ALIASES).filter((alias) => matrixLower.has(LEGACY_ROLE_ALIASES[alias].toLowerCase()));
        return [...matrixRoles, ...aliasRoles];
    }

    /** Roles (incluidos los alias) que tienen TODOS los permisos dados. */
    static rolesWithAll(permissions: readonly AppPermission[]): string[] {
        const matrixRoles = Object.keys(ROLE_PERMISSIONS).filter((role) => permissions.every((p) => ROLE_PERMISSIONS[role].includes(p)));
        const matrixLower = new Set(matrixRoles.map((role) => role.toLowerCase()));
        const aliasRoles = Object.keys(LEGACY_ROLE_ALIASES).filter((alias) => matrixLower.has(LEGACY_ROLE_ALIASES[alias].toLowerCase()));
        return [...matrixRoles, ...aliasRoles];
    }

    static canAccessApplication(roleOrRoles: string | string[]): boolean {
        return this.normalizeRoles(roleOrRoles).some((role) => NORMALIZED_ALLOWED_ADMIN_ROLES.has(role));
    }

    static isBlockedRole(roleOrRoles: string | string[]): boolean {
        return this.normalizeRoles(roleOrRoles).some((role) => NORMALIZED_BLOCKED_ROLES.has(role));
    }
}

function rolesForSection(section: SectionKey): string[] {
    const deniedRoles = new Set(SECTION_DENIED_ROLES[section] || []);
    return RoleManager.rolesWithAny(SECTION_PERMISSIONS[section]).filter((role) => !deniedRoles.has(role.toLowerCase()));
}

// ─────────────────────────────────────────────────────────────────────────────
// 6. SECTION_ROLES — derivado, para el `data.roles` de las rutas
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Listas de roles por sección que leen las rutas vía `data.roles` (las consume `RoleGuard`).
 *
 * Ya no se mantienen a mano: se derivan de `ROLE_PERMISSIONS`, así que un cambio en la matriz
 * se refleja solo. `RoleGuard` compara en minúsculas, que es justo cómo salen de aquí.
 */
export const SECTION_ROLES = {
    CLIENTS_READ: RoleManager.rolesWithAny(SECTION_PERMISSIONS.CLIENTS_READ),
    CLIENTS_WRITE: RoleManager.rolesWithAny(SECTION_PERMISSIONS.CLIENTS_WRITE),
    CLIENTS_EDIT: RoleManager.rolesWithAny(SECTION_PERMISSIONS.CLIENTS_EDIT),
    CLIENTS_REVIEW: RoleManager.rolesWithAny(SECTION_PERMISSIONS.CLIENTS_REVIEW),
    CLIENTS_DOCUMENTS_REVIEW: RoleManager.rolesWithAll(SECTION_PERMISSIONS.CLIENTS_DOCUMENTS_REVIEW),

    PROSPECTS_READ: RoleManager.rolesWithAny(SECTION_PERMISSIONS.PROSPECTS_READ),
    PROSPECTS_WRITE: RoleManager.rolesWithAny(SECTION_PERMISSIONS.PROSPECTS_WRITE),

    ORGANIZATIONS_READ: RoleManager.rolesWithAny(SECTION_PERMISSIONS.ORGANIZATIONS_READ),
    ORGANIZATIONS_WRITE: RoleManager.rolesWithAny(SECTION_PERMISSIONS.ORGANIZATIONS_WRITE),

    COMPLIANCE: rolesForSection('COMPLIANCE'),
    FINANCE_READ: rolesForSection('FINANCE_READ'),
    FINANCE_RETURNS: RoleManager.rolesWithAny(SECTION_PERMISSIONS.FINANCE_RETURNS),
    FINANCE_REPORTOS: RoleManager.rolesWithAny(SECTION_PERMISSIONS.FINANCE_REPORTOS),
    FINANCE_INVOICING: RoleManager.rolesWithAny(SECTION_PERMISSIONS.FINANCE_INVOICING),
    OPERATIONS_EXECUTE: RoleManager.rolesWithAny(SECTION_PERMISSIONS.OPERATIONS_EXECUTE),
    ROLES_ADMIN: RoleManager.rolesWithAny(SECTION_PERMISSIONS.ROLES_ADMIN)
};

/*
 * Nota: aquí vivía `ROLE_CONFIGS` / `RoleConfig` / `RolePermissionKey`, la capa vieja de 9
 * booleanos gruesos (`canAccessClients`, `canEdit`, …). Se eliminó al integrar la matriz del Excel:
 * ya nadie la alcanzaba — solo unos métodos de `AuthService` que ningún componente llamaba, y que
 * se migraron a `AuthService.can()` / `AuthService.canAccessSection()`.
 */
